import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
} from "@mui/material";
import CommonModal from "components/common/CommonModal";
import { useSessionClass } from "contexts/SessionClassContext";
import { useMarkSheets } from "hooks/useMarkSheets";
import { useAuth } from "hooks/useAuth";
import { useSchools } from "hooks/useSchools";
import { MARKSHEET_TYPE_OPTIONS } from "components/enum/MarksheetTypeOptions";
import { useTranslation } from "react-i18next";
import NoticeboardTemplate from "components/templates/NoticeboardTemplate";
import { ListAlt } from "@mui/icons-material";

const NoticeboardListManagement: React.FC = () => {
  const { t } = useTranslation();
  const { selectedClass, selectedSession } = useSessionClass();
  const { user } = useAuth();
  const isStudent = user?.roles?.includes('STUDENT');
  const { useSchoolById } = useSchools();
  const [marksheetType, setMarksheetType] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedExamType, setSelectedExamType] = useState("");

  // Get current school data
  const schoolId = user?.schoolId ?? undefined;
  const { data: currentSchool } = useSchoolById(schoolId);

  // Set default marksheet type based on class
  React.useEffect(() => {
    if (selectedClass && typeof selectedClass.name === "string") {
      const className = selectedClass.name.trim();
      if (className.startsWith("10th") || className.startsWith("12th")) {
        setMarksheetType("BOARD");
      } else {
        setMarksheetType("ANNUAL");
      }
    } else {
      setMarksheetType(""); // or "ANNUAL" as default if you prefer
    }
  }, [selectedClass]);

  // Prepare the payload for tcList
  const tcListPayload = useMemo(
    () => {
      const rawClassName = String(selectedClass?.name ?? "").trim();
      const isAllClass =
        (selectedClass?.id != null && Number(selectedClass.id) < 0) ||
        /\bALL\b$/i.test(rawClassName.replace(/-/g, " "));

      const classNameForApi = isAllClass
        ? (() => {
            if (/-ALL$/i.test(rawClassName)) return rawClassName.replace(/-all$/i, "-ALL");
            if (/\s+ALL$/i.test(rawClassName)) return rawClassName.replace(/\s+ALL$/i, "-ALL");
            return rawClassName ? `${rawClassName}-ALL` : "ALL";
          })()
        : rawClassName;

      return {
        schoolId: user?.schoolId ?? 0,
        classId: isAllClass ? null : (selectedClass?.id ?? 0),
        className: classNameForApi,
        sessionId: selectedSession?.id ?? 0,
        marksheetType,
      };
    },
    [user, selectedClass, selectedSession, marksheetType]
  );

  // Fetch tcList - disable marksheetsQuery for students
  const { useTcList } = useMarkSheets(false, isStudent);
  const {
    data: tcList,
    isLoading,
    error,
  } = useTcList(
    tcListPayload,
    !!user?.schoolId && !!selectedClass && !!selectedSession && !!marksheetType
  );

  const className = selectedClass?.name?.toLowerCase() || "";
  const isBoardClass =
    className.startsWith("10th") || className.startsWith("12th");

  const filteredOptions = MARKSHEET_TYPE_OPTIONS.filter((opt) => {
    if (isBoardClass) {
      // Show BOARD, PRE_BOARD, QUARTERLY, HALF_YEARLY
      return ["BOARD", "PRE_BOARD", "QUARTERLY", "HALF_YEARLY"].includes(
        opt.value
      );
    } else {
      // Show ANNUAL, QUARTERLY, HALF_YEARLY
      return ["ANNUAL", "QUARTERLY", "HALF_YEARLY"].includes(opt.value);
    }
  });

  const handleOpenDialog = () => {
    setSelectedExamType(marksheetType);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  

  // Sort notice board list by rank in ascending order (rank 1 is best)
  const sortedTcList = useMemo(() => {
    if (!tcList || tcList.length === 0) return [];
    
    const sorted = [...tcList].sort((a, b) => {
      // Get rank from backend data, default to a high number if rank is missing
      const rankA = (a as any).rank ?? Number.MAX_SAFE_INTEGER;
      const rankB = (b as any).rank ?? Number.MAX_SAFE_INTEGER;
      return rankA - rankB; // Ascending order (rank 1 first)
    });
    
    return sorted;
  }, [tcList]);

  const enrichedTcList = useMemo(() => {
    const parseNumeric = (value: any) => {
      if (value === null || value === undefined || value === "") return undefined;
      const num = typeof value === "string" ? Number(value) : value;
      return Number.isFinite(num) ? num : undefined;
    };

    const determineDivision = (student: any) => {
      // First, check if marksheet result exists from backend - use it directly
      const marksheetResult = student.result;
      
      // If marksheet result exists, use it and determine division based on percentage
      if (marksheetResult && (marksheetResult === "PASS" || marksheetResult === "FAIL")) {
        const totalMarks =
          parseNumeric(student.totalMarks) ??
          parseNumeric(student.totalmarks) ??
          parseNumeric(student.totalObtainedMarks);

        const possibleMaxKeys = [
          "totalMaxMarks",
          "maxMarks",
          "totalFullMarks",
          "totalPossibleMarks",
        ];

        let maxMarks: number | undefined;
        for (const key of possibleMaxKeys) {
          const value = parseNumeric(student[key]);
          if (value !== undefined) {
            maxMarks = value;
            break;
          }
        }

        let percentage = parseNumeric(student.percentage);
        if (
          (percentage === undefined || Number.isNaN(percentage)) &&
          totalMarks !== undefined &&
          maxMarks !== undefined &&
          maxMarks > 0
        ) {
          percentage = (totalMarks / maxMarks) * 100;
        }

        if (marksheetResult === "FAIL") {
          return { division: "FAIL", result: "FAIL" };
        }

        // If PASS, determine division based on percentage
        if (percentage === undefined || Number.isNaN(percentage)) {
          return { division: "", result: "PASS" };
        }

        if (percentage >= 60) {
          return { division: "FIRST DIVISION", result: "PASS" };
        }

        if (percentage >= 50) {
          return { division: "SECOND DIVISION", result: "PASS" };
        }

        return { division: "THIRD DIVISION", result: "PASS" };
      }

      // Fallback: Calculate based on percentage if marksheet result not available
      const totalMarks =
        parseNumeric(student.totalMarks) ??
        parseNumeric(student.totalmarks) ??
        parseNumeric(student.totalObtainedMarks);

      const possibleMaxKeys = [
        "totalMaxMarks",
        "maxMarks",
        "totalFullMarks",
        "totalPossibleMarks",
      ];

      let maxMarks: number | undefined;
      for (const key of possibleMaxKeys) {
        const value = parseNumeric(student[key]);
        if (value !== undefined) {
          maxMarks = value;
          break;
        }
      }

      let percentage = parseNumeric(student.percentage);
      if (
        (percentage === undefined || Number.isNaN(percentage)) &&
        totalMarks !== undefined &&
        maxMarks !== undefined &&
        maxMarks > 0
      ) {
        percentage = (totalMarks / maxMarks) * 100;
      }

      if (percentage === undefined || Number.isNaN(percentage)) {
        return { division: "", result: "" };
      }

      if (percentage < 33) {
        return { division: "FAIL", result: "FAIL" };
      }

      if (percentage >= 60) {
        return { division: "FIRST DIVISION", result: "PASS" };
      }

      if (percentage >= 50) {
        return { division: "SECOND DIVISION", result: "PASS" };
      }

      return { division: "THIRD DIVISION", result: "PASS" };
    };

    return (sortedTcList || []).map((student) => {
      const { division, result } = determineDivision(student);
      return {
        ...student,
        division,
        divisionResult: result,
        // Preserve the original marksheet result from backend
        result: student.result || result,
        // Preserve the rank from backend for sorting
        rank: (student as any).rank,
      };
    });
  }, [sortedTcList]);

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", mt: 2 }}>
      <Card sx={{ 
        p: 4, 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <ListAlt sx={{ fontSize: 28, color: '#81c784', mr: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
            {selectedClass?.name} {t("notice_board_list")}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#666', fontWeight: 500 }}>
              {t("exam_type")}
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={marksheetType}
                onChange={(e) => setMarksheetType(e.target.value)}
                sx={{
                  height: 40,
                  borderRadius: 1,
                  backgroundColor: '#ffffff',
                  border: '1px solid #ddd',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused': {
                    borderColor: 'primary.main',
                  }
                }}
              >
                {filteredOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ListAlt />}
            onClick={handleOpenDialog}
            disabled={
              !user?.schoolId ||
              !selectedClass ||
              !selectedSession ||
              !marksheetType
            }
          >
            {t("get_notice_board_list")}
          </Button>
        </Box>
      </Card>

      {(!user?.schoolId ||
      !selectedClass ||
      !selectedSession ||
      !marksheetType) && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            {t(
              "please_select_a_class_session_and_marksheet_type_to_view_the_notice_board_list"
            )}
          </Alert>
        </Box>
      )}

      {error && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      )}

      {/* Dialog for Notice Board Template */}
      <CommonModal
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={`${selectedClass?.name ?? ""} ${t("notice_board_list")} - ${selectedExamType}`}
        maxWidth="lg"
        fullWidth
        hideActions
        PaperProps={{ sx: { maxHeight: "90vh" } }}
        contentSx={{ p: 2, overflow: "auto" }}
      >
        {sortedTcList && sortedTcList.length > 0 ? (
          <NoticeboardTemplate
            noticeBoardData={enrichedTcList}
            schoolName={currentSchool?.nameofSchool || "School Name"}
            district={currentSchool?.district || "District"}
            block={currentSchool?.blockName || "Block"}
            diseCode={currentSchool?.diseCode || "DISE Code"}
            mpbseCode={currentSchool?.schoolCode || "School Code"}
            className={selectedClass?.name || ""}
            examType={selectedExamType}
            sessionYear={selectedSession?.name || ""}
            hidePrintButton={false}
          />
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No notice board data available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please check if there are students with marks for the selected
              exam type.
            </Typography>
          </Box>
        )}
      </CommonModal>
    </Box>
  );
};

export default NoticeboardListManagement;
